import { GoogleOAuthProvider } from '@react-oauth/google';
import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import { IntlProvider } from 'react-intl';

import enMessages from '@/assets/en.json';
import uaMessages from '@/assets/ua.json';
import { AuthProvider, useAuth } from '@/components/auth-provider';
import { LanguageProvider, useLanguage } from '@/components/language-provider';
import { routeTree } from '@/route-tree.gen';
import { Toaster } from '@/shared/ui/sonner';
import { Language } from '@/shared/utils';

Sentry.init({
  dsn: 'https://196bed4a283db24dc727bfeae44f35eb@o4509265846337536.ingest.us.sentry.io/4509413432033280',
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: true,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const queryClient = new QueryClient();

const router = createRouter({
  routeTree,
  context: {
    queryClient,
    auth: undefined!,
  },
  defaultPreload: 'intent',
  // Since we're using React Query, we don't want loader calls to ever be stale
  // This will ensure that the loader is always called when the route is preloaded or visited
  defaultPreloadStaleTime: 0,
});

const LOCALE_MESSAGES: Record<Language, Record<string, string>> = {
  en: enMessages,
  ua: uaMessages,
};

const LOCALES: Record<Language, string> = {
  en: 'en-US',
  ua: 'uk-UA',
};

const InnerApp = () => {
  const { language } = useLanguage();
  const auth = useAuth();
  const queryClient = useQueryClient();

  return (
    <IntlProvider
      messages={LOCALE_MESSAGES[language]}
      locale={LOCALES[language]}
      defaultLocale={LOCALES['en']}
    >
      <Toaster position="top-center" />

      <RouterProvider router={router} context={{ queryClient, auth }} />
    </IntlProvider>
  );
};

export function App() {
  return (
    <GoogleOAuthProvider clientId="790641498360-gu7sbg79at8haraeb3sot8b8mdfo0ags.apps.googleusercontent.com">
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <LanguageProvider>
            <InnerApp />
          </LanguageProvider>
        </AuthProvider>
      </QueryClientProvider>
    </GoogleOAuthProvider>
  );
}
